import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { aboutValues, milestones, stats, company } from "../mock";

export default function About() {
  return (
    <div className="pt-[72px]">
      {/* HERO */}
      <section className="relative py-28 lg:py-36 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-8">The Company</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl lg:text-[5rem] font-bold tracking-tight leading-[1.02] max-w-4xl">
              A single firm for <span className="gold-text">capital, coverage, and custody.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 text-stone-400 text-lg leading-relaxed max-w-2xl">
              Fidelity Federal Associates LLC was built on a simple conviction: insurance,
              investing, and property management should not be managed in isolation. We unite
              them under one disciplined, fiduciary standard.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow mb-5">Our mission</p>
              <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Outcomes engineered to endure.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <p className="text-stone-300 text-lg leading-relaxed">
                We exist to protect and grow the things our clients have worked to build. Every
                policy we structure, every dollar we allocate, and every property we steward is
                treated as part of one integrated balance sheet.
              </p>
              <p className="mt-6 text-stone-400 leading-relaxed">
                Headquartered in Greenville, South Carolina, our practice serves individuals,
                families, and operating businesses who value structure over speculation and
                partnership over transactions.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 divide-x divide-white/10">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className="py-12 px-4 lg:px-8">
                <p className="font-display text-3xl lg:text-5xl font-bold gold-text">{s.value}</p>
                <p className="mt-3 text-stone-400 text-xs lg:text-sm leading-snug">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-5">What we stand for</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-[1.05]">
              Principles, not slogans.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {aboutValues.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 100}>
                  <div className="border border-white/10 clip-corner p-8 h-full svc-card">
                    <Icon className="w-8 h-8 text-[#c8a951] mb-6" strokeWidth={1.5} />
                    <h3 className="font-display text-xl font-semibold mb-3">{v.title}</h3>
                    <p className="text-stone-400 text-sm leading-relaxed">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORY / TIMELINE */}
      <section className="py-28 border-t border-white/10 relative">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-5">Our story</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-[1.05]">
              How we operate.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-px bg-white/10 border border-white/10">
            {milestones.map((m, i) => (
              <Reveal key={m.year} delay={i * 80}>
                <div className="bg-[#08080a] p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center hover:bg-[#0d0d10] transition-colors">
                  <div className="md:col-span-3">
                    <span className="font-display text-xl font-semibold gold-text">{m.year}</span>
                  </div>
                  <div className="md:col-span-9">
                    <p className="text-stone-300 leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow mb-6">{company.tagline}</p>
            <h2 className="font-display text-4xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.05] max-w-3xl mx-auto">
              Work with a team that thinks in <span className="gold-text">decades.</span>
            </h2>
            <Link to="/contact" className="btn-gold clip-corner mt-10 inline-flex px-8 py-4 text-sm font-semibold items-center gap-2">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
